const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "";
const REQUEST_TIMEOUT_MS = 60000;

function getToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith("token="))
    ?.split("=")[1];
}

function setToken(token: string, maxAge = 30 * 24 * 60 * 60) {
  document.cookie = `token=${token}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearToken() {
  document.cookie = "token=; path=/; max-age=0";
}

function unwrapApiJson(body: unknown): unknown {
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return body;
  }
  const o = body as Record<string, unknown>;
  if (!("_sql_queries" in o)) {
    return body;
  }
  const { _sql_queries: _q, ...rest } = o;
  const keys = Object.keys(rest);
  if (keys.length === 1 && keys[0] === "data") {
    return rest.data;
  }
  if (keys.length === 1 && keys[0] === "result") {
    return rest.result;
  }
  return rest;
}

export async function request<T>(path: string, options?: RequestInit, timeoutMs?: number): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // Default de 60s serve quase toda chamada. Endpoints que sabidamente demoram
  // mais que isso (ex.: envio ao ERP com várias filiais, uma chamada SOAP em
  // série por filial) passam um `timeoutMs` maior explicitamente — sem isso o
  // navegador aborta a requisição bem antes do backend terminar de processá-la
  // (que continua rodando do mesmo jeito: abortar no cliente não cancela o
  // servidor), e a UI mostra um erro de timeout mesmo quando o envio segue OK.
  const timeout = timeoutMs ?? REQUEST_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Permite cancelar externamente (ex.: debounce de busca): combina o signal do
  // chamador com o controller interno (que também cuida do timeout).
  const external = options?.signal as AbortSignal | undefined;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", () => controller.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error) {
      if (err.name === "AbortError") {
        // Cancelamento explícito pelo chamador: propaga como AbortError p/ ser ignorado.
        if (external?.aborted) throw err;
        throw new Error("Servidor demorou para responder. Verifique sua conexão e a URL da API.");
      }
      if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
        throw new Error("Não foi possível conectar ao servidor. Verifique a URL da API e CORS.");
      }
    }
    throw err;
  }
  clearTimeout(timeoutId);

  const newToken = res.headers.get("X-New-Token");
  if (newToken) setToken(newToken);

  if (res.status === 401) {
    // 401 = sessão inválida/expirada → desloga e volta pro login.
    clearToken();
    if (token && typeof window !== "undefined") {
      window.location.href = "/login";
    }
    const body = await res.json().catch(() => ({}));
    const raw = body?.message ?? body?.detail;
    const msg =
      typeof raw === "string"
        ? raw
        : Array.isArray(raw) && raw[0] && typeof raw[0].msg === "string"
          ? raw[0].msg
          : "Sessão expirada";
    throw new Error(msg);
  }

  if (res.status === 403) {
    // 403 = autenticado, mas sem permissão para ESTE recurso.
    // Não deslogar: apenas propaga o erro para a página tratar.
    const body = await res.json().catch(() => ({}));
    const raw = body?.message ?? body?.detail;
    const msg =
      typeof raw === "string"
        ? raw
        : Array.isArray(raw) && raw[0] && typeof raw[0].msg === "string"
          ? raw[0].msg
          : "Você não tem permissão para acessar este recurso";
    throw new Error(msg);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body?.message || body?.detail || `Erro ${res.status}`;
    throw new Error(typeof msg === "string" ? msg : `Erro ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return unwrapApiJson(body) as T;
}

/**
 * Igual ao `request`, mas devolve um Blob (download de arquivo, ex.: Excel).
 * Mantém auth/token/erros; lê o nome do arquivo do Content-Disposition.
 * Timeout maior porque a geração server-side pode levar alguns segundos.
 */
export async function requestBlob(
  path: string,
  options?: RequestInit,
): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000);
  const external = options?.signal as AbortSignal | undefined;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", () => controller.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers, signal: controller.signal });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === "AbortError" && !external?.aborted) {
      throw new Error("Servidor demorou para gerar o arquivo.");
    }
    throw err;
  }
  clearTimeout(timeoutId);

  const newToken = res.headers.get("X-New-Token");
  if (newToken) setToken(newToken);

  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      if (token && typeof window !== "undefined") window.location.href = "/login";
    }
    const body = await res.json().catch(() => ({}));
    const raw = body?.message ?? body?.detail;
    const msg = typeof raw === "string" ? raw : `Erro ${res.status}`;
    throw new Error(msg);
  }

  const cd = res.headers.get("Content-Disposition") || "";
  // RFC 5987 (`filename*=UTF-8''nome%20com%20acentos.pdf`, usado por /reports/*
  // para nomes com acento) tem prioridade; senão cai no `filename="..."` simples.
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = cd.match(/filename="?([^";]+)"?/i);
  const raw = star?.[1] ?? plain?.[1];
  const filename = raw ? decodeURIComponent(raw) : "relatorio.xlsx";
  const blob = await res.blob();
  return { blob, filename };
}

/** Upload multipart/form-data (não define Content-Type — o browser monta o boundary). */
export async function requestFormData<T>(path: string, formData: FormData): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: formData });
  const newToken = res.headers.get("X-New-Token");
  if (newToken) setToken(newToken);

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body?.message || body?.detail || `Erro ${res.status}`;
    throw new Error(typeof msg === "string" ? msg : `Erro ${res.status}`);
  }
  const body = await res.json();
  return unwrapApiJson(body) as T;
}
